import { getSyncHook, request } from '@weapp/utils';

// Stores the domain of the icon resource. Initially set to null. 
let iconResourceDomainCache: string | null = null;
let iconVersionCache: string | null = null;
/**
 * readyIconResourceDomain is a function that returns a Promise of a string. 
 * It checks if window.TEAMS.ready is a function, and if so, calls it with the systemConfig parameter. 
 * If the response contains an iconResourceDomain, it is cached and the Promise is resolved with the iconResourceDomain. 
 * If the response does not contain an iconResourceDomain, the Promise is resolved with an empty string. 
 * If window.TEAMS.ready is not a function, the Promise is resolved with an empty string.
 */
const readyIconResourceDomain = () => {
  return new Promise<{ iconResourceDomain: string, iconVersion: string }>((resolve, reject) => {
    if (window?.TEAMS?.ready && typeof window.TEAMS.ready === 'function') {
      // 通过 TEAMS 获取
      window?.TEAMS?.ready?.('systemConfig', {
        success: (res: any) => {
          if (res?.data?.icon?.iconResourceDomain) {
            iconResourceDomainCache = res?.data?.icon?.iconResourceDomain;
            iconVersionCache = res?.data?.icon?.IconVersion;
            resolve({
              iconResourceDomain: iconResourceDomainCache || '',
              iconVersion: iconVersionCache || '',
            });
          } else {
            resolve({
              iconResourceDomain: '',
              iconVersion: ''
            })
          }
        },
        failed: (error: any) => {
          resolve({
            iconResourceDomain: '',
            iconVersion: ''
          })
        }
      });
    } else {
      resolve({
        iconResourceDomain: '',
        iconVersion: ''
      })
    }
  })
}

/**
 * requestIconResourceDoamin() is a function that makes a GET request to the '/papi/bcw/systemConfig/getSystemConfig' endpoint.
 * If the response is successful, it will resolve the promise with the iconResourceDomain from the response data.
 * If the response is unsuccessful, it will resolve the promise with an empty string.
 */
const requestIconResourceDoamin = () => {
  return new Promise<{ iconResourceDomain: string, iconVersion: string }>((resolve, reject) => {
    request({
      url: '/papi/bcw/systemConfig/getSystemConfig',
      method: 'GET'
    }).then(res => {
      if (res.status && res.data?.icon?.iconResourceDomain) {
        iconResourceDomainCache = res.data.icon.iconResourceDomain;
        iconVersionCache = res.data.icon.IconVersion;
        resolve({
          iconResourceDomain: iconResourceDomainCache || '',
          iconVersion: iconVersionCache || '',
        });
      } else {
        resolve({
          iconResourceDomain: '',
          iconVersion: ''
        })
      }
    }).catch(err => {
      resolve({
        iconResourceDomain: '',
        iconVersion: ''
      })
    })
  })
}

/**
 * Returns a promise that resolves to the icon resource domain.
 * The icon resource domain is first retrieved from the window.TEAMS.systemConfig.icon.iconResourceDomain,
 * then from the readyIconResourceDomain() function, and finally from the requestIconResourceDoamin() function.
 * The icon resource domain is cached for future use.
 */
export const getSystemConfigPromise = (): Promise<{ iconResourceDomainCache: string, iconVersionCache: string | null }> => {
  return new Promise(async (resolve) => {
    const hooks = getSyncHook('weappUi', 'Icon.resource.domain.overwrite');
    if (typeof hooks === 'function') {
      const { domain, version } = hooks(0);
      iconResourceDomainCache = domain as string;
      iconVersionCache = version;
      resolve({ iconResourceDomainCache, iconVersionCache })
    } else {
      if (iconResourceDomainCache) {
        resolve({ iconResourceDomainCache, iconVersionCache });
      } else {
        let iconResourceDomain: string = window?.TEAMS?.systemConfig?.icon?.iconResourceDomain || '';
        let iconVersion: string = window?.TEAMS?.systemConfig?.icon?.IconVersion || '';
        if (!iconResourceDomain) {
          const { iconResourceDomain: d, iconVersion: v } = await readyIconResourceDomain();
          iconResourceDomain = d;
          iconVersion = v;
        }
        if (!iconResourceDomain) {
          const { iconResourceDomain: d, iconVersion: v } = await requestIconResourceDoamin();
          iconResourceDomain = d;
          iconVersion = v;
        }
        // 从系统配置获取
        iconResourceDomainCache = iconResourceDomain;
        iconVersionCache = iconVersion;
        resolve({ iconResourceDomainCache, iconVersionCache });
      }
    }
  })
}